import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  FiSearch, FiClock, FiRefreshCw, FiMessageSquare,
  FiThumbsUp, FiThumbsDown, FiAlertTriangle, FiX,
} from 'react-icons/fi';
import '../commissionscan/CommissionScan.css';
import '../homeowners/homeowners.css';
import PreviewIcon from '../../assets/images/ViewDetailsIcon.svg';
import { useToast } from '../../context/ToastContext';

const PAGE_SIZE    = 10;
const getToken     = () => localStorage.getItem('toolboxToken') || '';
const getNcToken   = () => localStorage.getItem('amsToken')     || '';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  Success: { label: 'Success', color: '#34d399' },
  Failed: { label: 'Failed', color: '#f87171' },
};

const formatDate = (d: string): string => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${m[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
  } catch { return d; }
};

interface HistoryRow {
  transactionId: string;
  userName: string;
  status: string;
  isDriverAddedInAMS: boolean;
  isVehicleAddedInAMS: boolean;
  isLiked: boolean | null;
  created: string;
  totalCount: number;
  responseJson: string;
  requestJson: string;
}

interface Driver {
  firstName: string; lastName: string; dateOfBirth: string; age: number; gender: string;
}
interface Vehicle {
  vin: string; year: number; make: string; model: string;
  bodyStyle: string; registeredState: string; expirationDate: string;
}

const FenrisDriverVehicleHistory: React.FC = () => {
  const [rows, setRows]             = useState<HistoryRow[]>([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [search, setSearch]         = useState('');
  const [page, setPage]             = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  /* Preview modal */
  const [previewRow,   setPreviewRow]   = useState<HistoryRow | null>(null);
  const [previewDrivers,   setPreviewDrivers]   = useState<Driver[]>([]);
  const [previewVehicles,  setPreviewVehicles]  = useState<Vehicle[]>([]);
  const [previewInsuredId, setPreviewInsuredId] = useState('');
  const [previewFirstName, setPreviewFirstName] = useState('');
  const [previewLastName,  setPreviewLastName]  = useState('');
  const [previewTab,       setPreviewTab]       = useState<'Driver' | 'Vehicle'>('Driver');
  const [previewDriverAdded,  setPreviewDriverAdded]  = useState(false);
  const [previewVehicleAdded, setPreviewVehicleAdded] = useState(false);
  const [addingLoading, setAddingLoading] = useState(false);

  /* Feedback popup */
  const [feedbackRowId,      setFeedbackRowId]      = useState<string | null>(null);
  const [feedbackText,       setFeedbackText]        = useState('');
  const [feedbackRating,     setFeedbackRating]      = useState<boolean | null>(null);
  const [feedbackAnchor,     setFeedbackAnchor]      = useState<{ top: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting]  = useState(false);
  const [feedbackError,      setFeedbackError]       = useState('');
  const feedbackRef = useRef<HTMLDivElement>(null);

  const { showSuccess, showError: showErr } = useToast();

  /* ── Fetch history ── */
  const fetchData = useCallback(async (pageNum: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/FenrisDriverAndVehicle/history?pageNumber=${pageNum}&recordCount=${PAGE_SIZE}`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      const data: HistoryRow[] = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
      setRows(data);
      setTotalCount(data.length > 0 ? (data[0].totalCount ?? data.length) : 0);
    } catch (e: any) {
      setError(e.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(page); }, [fetchData, page]);
  useEffect(() => { setPage(1); }, [search]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r => r.userName?.toLowerCase().includes(q) || r.status?.toLowerCase().includes(q));
  }, [rows, search]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageList = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce<(number | '…')[]>((acc, p, idx, arr) => {
      if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
      acc.push(p);
      return acc;
    }, []);

  /* ── Preview ── */
  const openPreview = useCallback((row: HistoryRow) => {
    setPreviewRow(row);
    setPreviewTab('Driver');
    setPreviewDriverAdded(row.isDriverAddedInAMS);
    setPreviewVehicleAdded(row.isVehicleAddedInAMS);
    try {
      const rj  = row.responseJson  ? JSON.parse(row.responseJson)  : {};
      const req = row.requestJson   ? JSON.parse(row.requestJson)   : {};
      const sel = req.selectionOptionsJson ? JSON.parse(req.selectionOptionsJson) : {};
      setPreviewDrivers(rj.drivers ?? []);
      setPreviewVehicles(rj.vehiclesEnhanced ?? []);
      setPreviewInsuredId(sel.insuredDatabaseId || '');
      setPreviewFirstName(req.person?.firstName || req.Person?.firstName || '');
      setPreviewLastName(req.person?.lastName   || req.Person?.lastName  || '');
    } catch {}
  }, []);

  const updateHistoryRecord = useCallback((driverAdded: boolean, vehicleAdded: boolean) => {
    if (!previewRow) return;
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/FenrisDriverAndVehicle/update`,
      {
        transactionId: previewRow.transactionId,
        submittedJsonData: JSON.stringify({ driverDetails: previewDrivers, vehicleDetails: previewVehicles }),
        isDriverAddedInAMS: driverAdded,
        isVehicleAddedInAMS: vehicleAdded,
      },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    ).catch(() => {});
  }, [previewRow, previewDrivers, previewVehicles]);

  const addDriversFromHistory = useCallback(async () => {
    if (!previewRow) return;
    setAddingLoading(true);
    try {
      const payload = previewDrivers.map(d => ({
        insured_database_id: previewInsuredId,
        first_name: d.firstName, last_name: d.lastName,
        license_number: '', date_of_birth: d.dateOfBirth, active: true,
      }));
      const res = await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/adddrivers?ncToken=${getNcToken()}`,
        payload,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      const results: any[] = Array.isArray(res.data) ? res.data : [];
      const successes = results.filter(r => String(r.status) === '1');
      const failures  = results.filter(r => String(r.status) !== '1');
      if (successes.length > 0) {
        setPreviewDriverAdded(true);
        if (previewRow) {
          axios.post(
            `${(process.env.REACT_APP_DataFabric_Url || '')}/api/FenrisDriverAndVehicle/update`,
            { transactionId: previewRow.transactionId, submittedJsonData: JSON.stringify({ driverDetails: previewDrivers, vehicleDetails: previewVehicles }), isDriverAddedInAMS: true, isVehicleAddedInAMS: previewVehicleAdded },
            { headers: { Authorization: `Bearer ${getToken()}` } },
          ).catch(() => {});
        }
        setRows(prev => prev.map(r => r.transactionId === previewRow!.transactionId ? { ...r, isDriverAddedInAMS: true } : r));
        showSuccess('Drivers successfully added in AMS');
      }
      if (failures.length > 0) {
        showErr(failures.map(r => r.message).join(' | '));
      }
    } catch { showErr('Failed to add drivers to AMS'); }
    finally { setAddingLoading(false); }
  }, [previewRow, previewDrivers, previewVehicles, previewInsuredId, previewVehicleAdded]);

  const addVehiclesFromHistory = useCallback(async () => {
    if (!previewRow) return;
    setAddingLoading(true);
    try {
      const payload = previewVehicles.map(v => ({
        insured_database_id: previewInsuredId,
        vinNumber: v.vin, vehicleManufacturer: v.make, vehicleType: v.bodyStyle,
        year: v.year, insured_first_name: previewFirstName, insured_last_name: previewLastName,
        model: v.model, registration_expiration_date: v.expirationDate,
        vehicleEditAdditional3: { registration_state: v.registeredState }, check: true,
      }));
      const res = await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/addvehicles?ncToken=${getNcToken()}`,
        payload,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      const results: any[] = Array.isArray(res.data) ? res.data : [];
      const successes = results.filter(r => String(r.status) === '1');
      const failures  = results.filter(r => String(r.status) !== '1');
      if (successes.length > 0) {
        setPreviewVehicleAdded(true);
        if (previewRow) {
          axios.post(
            `${(process.env.REACT_APP_DataFabric_Url || '')}/api/FenrisDriverAndVehicle/update`,
            { transactionId: previewRow.transactionId, submittedJsonData: JSON.stringify({ driverDetails: previewDrivers, vehicleDetails: previewVehicles }), isDriverAddedInAMS: previewDriverAdded, isVehicleAddedInAMS: true },
            { headers: { Authorization: `Bearer ${getToken()}` } },
          ).catch(() => {});
        }
        setRows(prev => prev.map(r => r.transactionId === previewRow!.transactionId ? { ...r, isVehicleAddedInAMS: true } : r));
        showSuccess('Vehicles successfully added in AMS');
      }
      if (failures.length > 0) {
        showErr(failures.map(r => r.message).join(' | '));
      }
    } catch { showErr('Failed to add vehicles to AMS'); }
    finally { setAddingLoading(false); }
  }, [previewRow, previewDrivers, previewVehicles, previewInsuredId, previewFirstName, previewLastName, previewDriverAdded]);

  /* ── Feedback ── */
  const openFeedback = useCallback((row: HistoryRow, e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackRowId(row.transactionId);
    setFeedbackText('');
    setFeedbackRating(row.isLiked ?? null);
    setFeedbackError('');
    setFeedbackAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  const closeFeedback = useCallback(() => {
    setFeedbackRowId(null); setFeedbackAnchor(null);
    setFeedbackText(''); setFeedbackRating(null); setFeedbackError('');
  }, []);

  const saveRating = useCallback((id: string, isLiked: boolean | null, feedback = '') => {
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAICommissionStatement/feedback`,
      { Target: 6, TransactionId: id, IsLiked: isLiked, Feedback: feedback },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    ).then(() => showSuccess('Feedback submitted successfully.')).catch(() => {});
  }, []);

  const onThumbsUp = useCallback(() => {
    if (!feedbackRowId) return;
    const cur = rows.find(r => r.transactionId === feedbackRowId);
    const next: boolean | null = cur?.isLiked === true ? null : true;
    setFeedbackRating(next);
    setRows(prev => prev.map(r => r.transactionId === feedbackRowId ? { ...r, isLiked: next } : r));
    saveRating(feedbackRowId, next);
    closeFeedback();
  }, [feedbackRowId, rows, saveRating, closeFeedback]);

  const onThumbsDown = useCallback(() => {
    if (!feedbackRowId) return;
    const cur = rows.find(r => r.transactionId === feedbackRowId);
    const next: boolean | null = cur?.isLiked === false ? null : false;
    setFeedbackRating(next);
    setRows(prev => prev.map(r => r.transactionId === feedbackRowId ? { ...r, isLiked: next } : r));
    saveRating(feedbackRowId, next);
    closeFeedback();
  }, [feedbackRowId, rows, saveRating, closeFeedback]);

  const onSubmitFeedback = useCallback(() => {
    if (!feedbackRowId) return;
    if (!feedbackText.trim()) { setFeedbackError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFeedbackError('Max 250 characters'); return; }
    setFeedbackError('');
    setFeedbackSubmitting(true);
    const cur = rows.find(r => r.transactionId === feedbackRowId);
    saveRating(feedbackRowId, cur?.isLiked ?? feedbackRating, feedbackText);
    setFeedbackSubmitting(false);
    closeFeedback();
  }, [feedbackRowId, feedbackText, feedbackRating, rows, saveRating, closeFeedback]);

  useEffect(() => {
    if (!feedbackRowId) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) closeFeedback();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackRowId, closeFeedback]);

  const formatDate2 = (d: string) => {
    if (!d) return '—';
    try {
      const dt = new Date(d);
      return `${(dt.getMonth()+1).toString().padStart(2,'0')}/${dt.getDate().toString().padStart(2,'0')}/${dt.getFullYear()} ${dt.getHours().toString().padStart(2,'0')}:${dt.getMinutes().toString().padStart(2,'0')}`;
    } catch { return d; }
  };

  /* ════════════ RENDER ════════════ */
  return (
    <>
      <motion.div className="qp-history" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
        <div className="qp-history__header">
          <h3 className="qp-history__title" style={{ margin: 0 }}>Driver &amp; Vehicle History</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="cs-client-search__input-wrap" style={{ minWidth: '220px' }}>
              <FiSearch size={13} className="cs-client-search__icon" />
              <input className="cs-client-search__input" type="text" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <motion.button className="csh-refresh-btn" onClick={() => fetchData(page)} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <FiRefreshCw size={13} className={loading && rows.length > 0 ? 'csh-spin' : ''} />
              Refresh
            </motion.button>
          </div>
        </div>

        <div className="qp-history__body">
          {error ? (
            <div className="qp-empty-state" style={{ color: 'var(--accent-red)' }}><FiClock size={28} /><p>{error}</p></div>
          ) : loading ? (
            <div className="qp-history-loader" style={{ minHeight: 280 }}>
              <div className="qp-history-loader__orb">
                <div className="qp-history-loader__ring qp-history-loader__ring--1" />
                <div className="qp-history-loader__ring qp-history-loader__ring--2" />
                <div className="qp-history-loader__core"><FiSearch size={16} /></div>
              </div>
              <div className="qp-history-loader__text">
                <p className="qp-history-loader__title">Loading History</p>
                <p className="qp-history-loader__sub">Fetching driver &amp; vehicle records<span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span></p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="qp-empty-state">
              <FiClock size={28} />
              <p>{search ? 'No records match your search.' : 'No driver & vehicle lookups yet.'}</p>
            </div>
          ) : (
            <div className="qp-table-wrap glass-surface">
              <table className="qp-table">
                <thead>
                  <tr>
                    <th className="qp-table__th">Submitted Date</th>
                    <th className="qp-table__th">User</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Driver in AMS</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Vehicle in AMS</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Status</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Action</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row, i) => (
                    <tr key={row.transactionId ?? i} className="qp-table__row">
                      <td className="qp-table__td" style={{ whiteSpace: 'nowrap' }}>{formatDate2(row.created)}</td>
                      <td className="qp-table__td">{row.userName || '—'}</td>
                      <td className="qp-table__td" style={{ textAlign: 'center' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: row.isDriverAddedInAMS ? 'rgba(52,211,153,0.12)' : 'rgba(239,68,68,0.1)', color: row.isDriverAddedInAMS ? '#34d399' : '#f87171' }}>
                          {row.isDriverAddedInAMS ? '✓ Yes' : '✗ No'}
                        </span>
                      </td>
                      <td className="qp-table__td" style={{ textAlign: 'center' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: row.isVehicleAddedInAMS ? 'rgba(52,211,153,0.12)' : 'rgba(239,68,68,0.1)', color: row.isVehicleAddedInAMS ? '#34d399' : '#f87171' }}>
                          {row.isVehicleAddedInAMS ? '✓ Yes' : '✗ No'}
                        </span>
                      </td>
                      <td className="qp-table__td" style={{ textAlign: 'center' }}>
                        <span className="qp-status-pill" style={{ color: (STATUS_CONFIG[row.status] || STATUS_CONFIG.Failed).color }}>
                          {(STATUS_CONFIG[row.status] || { label: row.status }).label}
                        </span>
                      </td>
                      <td className="qp-table__td" style={{ textAlign: 'center' }}>
                        {row.status === 'Success' ? (
                          <button className="qp-table__action-btn" title="Preview" onClick={() => openPreview(row)}>
                            <img src={PreviewIcon} alt="Preview" width={30} height={30} />
                          </button>
                        ) : (
                          <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Not Available</span>
                        )}
                      </td>
                      <td className="qp-table__td" style={{ textAlign: 'center' }}>
                        <button className="qp-table__action-btn" title="Feedback" onClick={e => openFeedback(row, e)} style={{ margin: '0 auto' }}>
                          <FiMessageSquare size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="qp-pagination">
              <span className="qp-pagination__info">{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}</span>
              <div className="qp-pagination__controls">
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(1)} title="First">«</button>
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} title="Previous">‹</button>
                {pageList.map((p, i) =>
                  p === '…'
                    ? <span key={`e-${i}`} className="qp-pagination__ellipsis">…</span>
                    : <button key={p} className={`qp-pagination__btn${page === p ? ' qp-pagination__btn--active' : ''}`} onClick={() => setPage(p as number)}>{p}</button>
                )}
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} title="Next">›</button>
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(totalPages)} title="Last">»</button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* ── Feedback popup (portalled) ── */}
      {feedbackRowId && feedbackAnchor && ReactDOM.createPortal(
        <div ref={feedbackRef} className="csfb-popup" style={{ top: feedbackAnchor.top, right: feedbackAnchor.right }}>
          <h4 className="csfb-title">Feedback</h4>
          <textarea className="csfb-textarea" placeholder="Enter your feedback here" value={feedbackText} onChange={e => setFeedbackText(e.target.value)} rows={4} />
          {feedbackError && <span style={{ fontSize: 11, color: 'var(--accent-red, #ef4444)', marginTop: 4, display: 'block' }}>{feedbackError}</span>}
          <div className="csfb-rating">
            <button className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`} onClick={onThumbsUp} title="Thumbs up"><FiThumbsUp size={20} /></button>
            <button className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`} onClick={onThumbsDown} title="Thumbs down"><FiThumbsDown size={20} /></button>
          </div>
          <div className="csfb-actions">
            <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
            <button className="csfb-btn-submit" disabled={feedbackSubmitting} onClick={onSubmitFeedback}>{feedbackSubmitting ? 'Submitting…' : 'Submit'}</button>
          </div>
        </div>,
        document.body,
      )}

      {/* ── Preview modal (portalled) ── */}
      {previewRow && ReactDOM.createPortal(
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'var(--modal-overlay, rgba(0,0,0,0.75))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setPreviewRow(null)}
        >
          <div className="ho-modal" onClick={e => e.stopPropagation()} style={{ width: '75vw', maxWidth: 900 }}>
            <div className="ho-modal__header">
              <div className="ho-modal__title-row">
                <h3 className="ho-modal__title">Driver &amp; Vehicle Details</h3>
              </div>
              <button className="ho-modal__close" onClick={() => setPreviewRow(null)}><FiX size={16} /></button>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4, padding: '0 20px', borderBottom: '1px solid var(--border-glass)' }}>
              {(['Driver', 'Vehicle'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setPreviewTab(tab)}
                  style={{
                    padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    background: 'none', border: 'none',
                    borderBottom: previewTab === tab ? '2px solid var(--accent-purple, #a200ff)' : '2px solid transparent',
                    color: previewTab === tab ? 'var(--accent-purple, #a200ff)' : 'var(--text-secondary)',
                  }}
                >
                  {tab} Details {tab === 'Driver' ? `(${previewDrivers.length})` : `(${previewVehicles.length})`}
                  {tab === 'Driver' && previewDriverAdded && <span style={{ marginLeft: 6, fontSize: 10, color: '#34d399' }}>✓ In AMS</span>}
                  {tab === 'Vehicle' && previewVehicleAdded && <span style={{ marginLeft: 6, fontSize: 10, color: '#34d399' }}>✓ In AMS</span>}
                </button>
              ))}
            </div>

            <div className="ho-modal__body">
              {previewTab === 'Driver' && (
                previewDrivers.length === 0 ? (
                  <div className="qp-empty-state" style={{ padding: '32px 0' }}><FiAlertTriangle size={22} /><p>No driver data available.</p></div>
                ) : (
                  <div className="qp-table-wrap glass-surface">
                    <table className="qp-table">
                      <thead><tr><th className="qp-table__th">First Name</th><th className="qp-table__th">Last Name</th><th className="qp-table__th">Date of Birth</th><th className="qp-table__th">Age</th><th className="qp-table__th">Gender</th></tr></thead>
                      <tbody>
                        {previewDrivers.map((d, i) => (
                          <tr key={i} className="qp-table__row">
                            <td className="qp-table__td">{d.firstName}</td>
                            <td className="qp-table__td">{d.lastName}</td>
                            <td className="qp-table__td">{formatDate(d.dateOfBirth)}</td>
                            <td className="qp-table__td">{d.age}</td>
                            <td className="qp-table__td">{d.gender}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
              {previewTab === 'Vehicle' && (
                previewVehicles.length === 0 ? (
                  <div className="qp-empty-state" style={{ padding: '32px 0' }}><FiAlertTriangle size={22} /><p>No vehicle data available.</p></div>
                ) : (
                  <div className="qp-table-wrap glass-surface">
                    <table className="qp-table">
                      <thead><tr><th className="qp-table__th">VIN</th><th className="qp-table__th">Year</th><th className="qp-table__th">Make</th><th className="qp-table__th">Model</th><th className="qp-table__th">Body Style</th><th className="qp-table__th">Reg. State</th><th className="qp-table__th">Expiration</th></tr></thead>
                      <tbody>
                        {previewVehicles.map((v, i) => (
                          <tr key={i} className="qp-table__row">
                            <td className="qp-table__td" style={{ fontFamily: 'monospace', fontSize: 11 }}>{v.vin}</td>
                            <td className="qp-table__td">{v.year}</td>
                            <td className="qp-table__td">{v.make}</td>
                            <td className="qp-table__td">{v.model}</td>
                            <td className="qp-table__td">{v.bodyStyle}</td>
                            <td className="qp-table__td">{v.registeredState}</td>
                            <td className="qp-table__td">{formatDate(v.expirationDate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </div>

            <div className="ho-modal__footer" style={{ justifyContent: 'flex-end' }}>
              <button onClick={() => setPreviewRow(null)} style={{ padding: '8px 20px', fontSize: 13, fontWeight: 600, background: 'var(--surface-glass)', color: 'var(--text-secondary)', border: '1px solid var(--border-glass)', borderRadius: 8, cursor: 'pointer' }}>Close</button>
              {previewTab === 'Driver' && !previewDriverAdded && previewDrivers.length > 0 && (
                <motion.button className="ho-search-btn" onClick={addDriversFromHistory} disabled={addingLoading} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  {addingLoading ? 'Adding…' : 'Add Drivers to AMS'}
                </motion.button>
              )}
              {previewTab === 'Vehicle' && !previewVehicleAdded && previewVehicles.length > 0 && (
                <motion.button className="ho-search-btn" onClick={addVehiclesFromHistory} disabled={addingLoading} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  {addingLoading ? 'Adding…' : 'Add Vehicles to AMS'}
                </motion.button>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default FenrisDriverVehicleHistory;

